import React from "react";
import { Link } from "react-router-dom";
import { Infinity as InfinityIcon } from "lucide-react";

const SUPPORT_EMAIL = "support@always-n-anytime.com";

const POLICIES = {
  shipping: {
    tag: "Help Center",
    title: "Shipping & Returns",
    updated: "Updated June 2026",
    intro:
      "Every Always-N-Anytime piece is printed to order the moment you check out \u2014 zero inventory, zero waste, made fresh for you. Here's exactly how fulfillment, shipping and returns work.",
    sections: [
      {
        h: "Made-to-order production",
        body: [
          "Because we're print-on-demand, each item is produced after you order. Production (print + quality check) typically takes 2\u20137 business days before your order leaves the facility.",
          "During drops and holidays production can run slightly longer \u2014 we'll always email you if anything is delayed.",
        ],
      },
      {
        h: "Shipping times",
        body: [
          "United States: 3\u20138 business days after production.",
          "Canada & Europe: 6\u201320 business days after production.",
          "Rest of world: 10\u201330 business days after production.",
          "Exact shipping rates are calculated at checkout based on your address and the items in your cart.",
        ],
      },
      {
        h: "Tracking",
        body: [
          "You'll get a confirmation email when you order and a second email with tracking the moment your package ships. Orders with multiple items may ship in separate parcels at no extra cost to you.",
        ],
      },
      {
        h: "Returns & exchanges",
        body: [
          "Since every item is custom-printed for you, we don't accept returns for buyer's remorse or for ordering the wrong size \u2014 please check the size guide before you order.",
          "We will happily replace or refund any item that arrives defective, misprinted, damaged, or if you received the wrong product. Just email us within 30 days of delivery with your order number and a photo of the issue.",
          "Approved replacements are reprinted and reshipped free of charge.",
        ],
      },
      {
        h: "Lost or damaged in transit",
        body: [
          "If your order is lost or damaged on its way to you, we've got you. Reach out and we'll reprint and reship or issue a refund \u2014 no drama.",
        ],
      },
      {
        h: "Need a hand?",
        body: [
          `Email us any time at ${SUPPORT_EMAIL} and a real human will get back to you within 1\u20132 business days.`,
        ],
      },
    ],
  },
  faq: {
    tag: "Help Center",
    title: "FAQ",
    updated: "Updated June 2026",
    intro: "Quick answers to the things people ask us most.",
    sections: [
      {
        h: "Are your products really made to order?",
        body: [
          "Yep. Nothing sits in a warehouse. When you buy, we print it \u2014 that's why our stuff stays limited and low-waste.",
        ],
      },
      {
        h: "How do the sizes fit?",
        body: [
          "Our tees and hoodies run boxy / oversized by design. If you want a trimmer fit, size down. Every product page links the full size guide.",
        ],
      },
      {
        h: "Can I cancel or change my order?",
        body: [
          "Reach out within 12 hours of ordering and we'll do our best to catch it before it goes to print. After it's in production we can't change it.",
        ],
      },
      {
        h: "Do you ship worldwide?",
        body: ["We do \u2014 shipping and rates are shown at checkout for your country."],
      },
      {
        h: "How do I track my order?",
        body: ["You'll get a tracking link by email as soon as your order ships."],
      },
    ],
  },
  terms: {
    tag: "Legal",
    title: "Terms of Service",
    updated: "Updated June 2026",
    intro:
      "By using always-n-anytime.com and placing an order, you agree to these terms. Keep it simple, keep it real.",
    sections: [
      {
        h: "Orders & pricing",
        body: [
          "All prices are in USD. We reserve the right to correct pricing errors and to cancel any order affected by an obvious error, with a full refund.",
          "Placing an order is an offer to buy; your order is confirmed once payment is captured and you receive a confirmation email.",
        ],
      },
      {
        h: "Made-to-order items",
        body: [
          "Most items are printed on demand and are therefore final sale except in cases of defect, misprint, damage, or a wrong item \u2014 see our Shipping & Returns policy.",
        ],
      },
      {
        h: "Intellectual property",
        body: [
          "All artwork, logos, the infinity mark, and brand names (Always-N-Anytime, Sabotage, Boothill Bottoms, Stand On It, Hardtimez) are owned by Always-N-Anytime and may not be reproduced without permission.",
        ],
      },
      {
        h: "Limitation of liability",
        body: [
          "Our products are sold as-is for personal use. To the fullest extent permitted by law, our liability for any claim is limited to the amount you paid for the item in question.",
        ],
      },
    ],
  },
  privacy: {
    tag: "Legal",
    title: "Privacy Policy",
    updated: "Updated June 2026",
    intro:
      "We only collect what we need to get your order to you and to keep you posted on drops. We never sell your data.",
    sections: [
      {
        h: "What we collect",
        body: [
          "Contact and shipping details you provide at checkout (name, email, address), and your order history. Newsletter signups store only your email.",
          "Payments are processed securely by Stripe \u2014 we never see or store your full card number.",
        ],
      },
      {
        h: "How we use it",
        body: [
          "To fulfill and ship your orders, send order and shipping updates, provide support, and \u2014 if you opt in \u2014 tell you about new drops. That's it.",
        ],
      },
      {
        h: "Who we share it with",
        body: [
          "Only the partners required to fulfill your order (our print & shipping providers and our payment processor). We do not sell or rent your personal information to anyone.",
        ],
      },
      {
        h: "Your choices",
        body: [
          `You can unsubscribe from emails any time via the link in any email, or request access to or deletion of your data by writing to ${SUPPORT_EMAIL}.`,
        ],
      },
    ],
  },
};

export default function Policy({ kind = "shipping" }) {
  const doc = POLICIES[kind] || POLICIES.shipping;
  return (
    <main className="bg-black text-white min-h-screen" data-testid={`policy-page-${kind}`}>
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0d0d0d] to-black">
        <div className="max-w-3xl mx-auto px-4 md:px-8 pt-28 pb-14">
          <div className="flex items-center gap-2 text-neon text-xs font-bold uppercase tracking-[0.25em]">
            <InfinityIcon size={16} />
            <span>{doc.tag}</span>
          </div>
          <h1
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95]"
            data-testid="policy-title"
          >
            {doc.title}
          </h1>
          <p className="mt-4 text-white/60 text-sm">{doc.updated}</p>
          <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl">
            {doc.intro}
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 md:px-8 py-14">
        <div className="space-y-10">
          {doc.sections.map((s, i) => (
            <div key={s.h} className="border-l-2 border-neon/40 pl-5" data-testid={`policy-section-${i}`}>
              <h2 className="text-lg md:text-xl font-bold text-white mb-3">{s.h}</h2>
              <div className="space-y-2.5">
                {s.body.map((b, j) => (
                  <p key={`${s.h}-${j}`} className="text-sm md:text-base text-white/70 leading-relaxed">
                    {b}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 text-center">
          <p className="text-white/70 text-sm">Still have a question?</p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="policy-contact-email"
            className="mt-2 inline-block text-neon font-bold text-lg hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
          <div className="mt-6">
            <Link
              to="/shop"
              data-testid="policy-shop-cta"
              className="inline-block bg-neon text-black font-bold uppercase tracking-widest text-sm px-7 py-3 rounded-full hover:brightness-95 transition"
            >
              Back to the drop
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
